import React, { useMemo } from 'react'
import { PartnerService, UserService } from '@duck/api-definitions'
import { cn } from '@duck/ui'
import { useTar } from '@/modules/i18n'
import { RelativeDateTime } from '@/components/datetime/RelativeDateTime'
import { GradeBadge } from '@/components/GradeBadge/GradeBadge'
import { UserBadge } from '@/components/UserBadge/UserBadge'
import { useTableSettings } from '@/hooks/TableSettings/useTableSettings'
import { renderSortIcon } from '@/hooks/TableSettings/generalTable.utils'
import { sortRosterDetails, type RosterSortColumn } from '@/hooks/TableSettings/assignmentRoster.utils'
import { StatusPill } from '../shared/StatusPill'
import { PublicAssignmentEmptyState } from './PublicAssignmentEmptyState'
import { toResultsDetails, type ResultsDetails } from './SubmissionResultsDialog'

const S = PartnerService.AssignmentDetailsStatus

interface AssignmentRosterProps {
  details: PartnerService.AssignmentDetails[]
  // Directory entries (a structural superset of Person) are the lookup source for roster identity;
  // they also cover public-link joiners who are not in cohort.personIds.
  persons: UserService.PersonDirectoryEntry[]
  assignmentId: string
  onViewResults: (d: ResultsDetails) => void
  /** Public assignment: people join via the link, so a "Sent" status reads "Joined". */
  isPublic?: boolean
  /** Public share token — enables the copy-link CTA in the empty state. */
  linkToken?: string
}

export const AssignmentRoster: React.FC<AssignmentRosterProps> = ({ details, persons, assignmentId, onViewResults, isPublic, linkToken }) => {
  const { t } = useTar()
  const { sortColumn, sortDirection, toggleSort, getSortState } = useTableSettings({
    tableId: `cohortAssignmentRoster-${assignmentId}`,
    defaultSort: { columnKey: 'startDate', direction: 'desc' },
  })

  const rows = useMemo(
    () => sortRosterDetails(details, persons, sortColumn, sortDirection),
    [details, persons, sortColumn, sortDirection]
  )

  const sortButton = (col: RosterSortColumn, label: string) => (
    <button
      onClick={() => toggleSort(col)}
      className="group flex items-center gap-1 uppercase tracking-wider hover:text-foreground transition-colors"
    >
      <span>{label}</span>
      {renderSortIcon(getSortState(col))}
    </button>
  )

  // Empty roster: skip the bare column headers. A public assignment fills only once people open
  // its link, so surface a copy-link CTA instead of a lifeless header strip.
  if (details.length === 0) {
    return (
      <div className="border-t border-border bg-background/60">
        {isPublic && linkToken ? (
          <PublicAssignmentEmptyState linkToken={linkToken} />
        ) : (
          <p className="px-4 py-8 text-center text-sm text-muted-foreground">{t('partner.noAssignmentsYet')}</p>
        )}
      </div>
    )
  }

  return (
    <div className="border-t border-border bg-background/60">
      <div className="grid h-9 grid-cols-[1fr_auto] items-center gap-2 px-4 ps-4 text-[10px] uppercase tracking-wider text-muted-foreground lg:grid-cols-[1.6fr_1fr_1fr_140px] lg:ps-12">
        <span>{sortButton('name', t('common.name'))}</span>
        <span className="hidden lg:block">{sortButton('startDate', t('common.started'))}</span>
        <span className="hidden lg:block">{sortButton('submitDate', t('common.submitted'))}</span>
        <span className="flex items-center justify-end">
          {sortButton('status', t('common.status'))}
        </span>
      </div>
      {rows.map((d, i) => {
        const person = persons.find((p) => p.id === d.personId)
        const name = (person ? `${person.firstName || ''} ${person.lastName || ''}`.trim() : '') || d.personId
        const graded = !!d.feedback && d.grade !== undefined
        const processing = d.status === S.Submitted && !graded
        // A graded line is clickable anywhere to open its results (no separate link).
        const openResults = graded ? () => onViewResults(toResultsDetails(d, name, person)) : undefined
        return (
          <div
            key={`${d.personId}-${i}`}
            className={cn(
              'grid h-12 grid-cols-[1fr_auto] items-center gap-2 border-b border-border/60 px-4 ps-4 text-xs last:border-b-0 lg:grid-cols-[1.6fr_1fr_1fr_140px] lg:ps-12',
              graded && 'cursor-pointer hover:bg-primary/5'
            )}
            {...(graded
              ? {
                  role: 'button' as const,
                  tabIndex: 0,
                  onClick: openResults,
                  onKeyDown: (e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openResults?.() }
                  },
                }
              : {})}
          >
            <span className="min-w-0">
              <UserBadge
                userId={person?.userId ?? ''}
                firstName={person?.firstName}
                lastName={person?.lastName}
                fullName={name}
                email={person?.email}
                disableTooltip={graded}
              />
            </span>
            <span className="hidden text-foreground lg:block">{d.startDate ? <RelativeDateTime isoDateString={d.startDate} className="text-xs" /> : <span className="text-muted-foreground">—</span>}</span>
            <span className="hidden text-foreground lg:block">{d.submitDate ? <RelativeDateTime isoDateString={d.submitDate} className="text-xs" /> : <span className="text-muted-foreground">—</span>}</span>
            <span className="flex items-center justify-end gap-2">
              {graded ? (
                <GradeBadge grade={d.grade as number} small />
              ) : (
                <StatusPill status={d.status} processing={processing} isPublic={isPublic} />
              )}
            </span>
          </div>
        )
      })}
    </div>
  )
}
